import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiLogOut } from "react-icons/fi";
import { CURRENT_VERSION } from "./releaseNotes";
import { PORTAL_LABELS as SECTION_LABELS } from "./portalHelpers";
import { navItemIsActive, pathMatchesNavItem } from "./filterPortalMenuItems";
import { usePaymentHold } from "./PlanPaymentHold";
import { confirmPortalLogout } from "../../shared/portalLogoutConfirm";

const SECTION_ORDER = [
  "overview",
  "performance",
  "skin",
  "hair",
  "operations",
  "store",
  "administration",
  "catalog-setup",
  "kiosk",
  "catalog",
  "network",
  "insights",
  "access",
  "organization",
  "about",
];

const PortalSidebar = ({
  menuItems = [],
  releaseNotesPath,
  versionLabel,
  logoSrc,
  logoFallback,
  isFranchise = false,
  expanded,
  setExpanded,
  onLogout,
  isCompactViewport = false,
  locationPathname = "",
}) => {
  const { hold, paused } = usePaymentHold();
  const lockMenu = Boolean(paused && !hold?.renewalLapsed);
  const items = useMemo(
    () => (lockMenu ? menuItems.map((item) => ({ ...item, disabled: true })) : menuItems),
    [menuItems, lockMenu],
  );
  const menuGroups = useMemo(() => {
    const byId = new Map();
    for (const item of items) {
      const id = item.sectionId || "overview";
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          label: SECTION_LABELS[id] || id,
          items: [],
        });
      }
      byId.get(id).items.push(item);
    }
    const seen = new Set();
    const ordered = [];
    for (const id of SECTION_ORDER) {
      if (seen.has(id) || !byId.has(id)) continue;
      seen.add(id);
      ordered.push(byId.get(id));
    }
    for (const [id, group] of byId) {
      if (!seen.has(id)) ordered.push(group);
    }
    return ordered;
  }, [items]);

  const closeIfCompact = () => {
    if (isCompactViewport && typeof setExpanded === "function") {
      setExpanded(false);
    }
  };

  const onLogoutClick = async () => {
    if (typeof onLogout !== "function") return;
    const ok = await confirmPortalLogout({
      title: "Sign out?",
      description: "You will need to sign in again to continue.",
      confirmLabel: "Sign out",
    });
    if (ok) onLogout();
  };

  const renderNavLink = (item) => (
    <NavLink
      key={item.key}
      to={item.disabled ? "#" : item.to}
      onClick={(e) => {
        if (item.disabled) {
          e.preventDefault();
          return;
        }
        closeIfCompact();
      }}
      isActive={({ location }) => navItemIsActive(location.pathname, item)}
      className={({ isActive }) =>
        `portal-nav-link${item.disabled ? " portal-nav-link--disabled" : ""}${
          isActive ? " portal-nav-link--active" : ""
        }`
      }
      title={item.disabled ? (lockMenu ? "Complete payment to continue" : "Temporarily disabled") : item.label}
    >
      {item.IconComponent ? (
        <item.IconComponent className="portal-nav-link__icon" strokeWidth={2.5} aria-hidden="true" />
      ) : item.icon ? (
        <img src={item.icon} alt="" className="portal-nav-link__icon-img" />
      ) : null}
      {expanded ? <span className="portal-nav-link__label">{item.label}</span> : null}
    </NavLink>
  );

  return (
    <>
      {isCompactViewport && expanded ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setExpanded(false)}
        />
      ) : null}
      <aside
        className={`portal-sidebar ${expanded ? "portal-sidebar--expanded" : "portal-sidebar--collapsed"}`}
      >
        <div className="portal-sidebar__top">
          <div className="portal-sidebar__brand">
            <div className="portal-sidebar__logo">
              <img
                key={logoSrc}
                src={logoSrc}
                alt="Organization logo"
                onError={(e) => {
                  if (!logoFallback || e.currentTarget.dataset.fallback === "1") return;
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = logoFallback;
                }}
              />
            </div>
          </div>
          <button
            type="button"
            className="portal-sidebar__toggle"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {expanded ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
          </button>
        </div>

        <nav className="portal-sidebar__nav" aria-label="Main navigation">
          {!expanded
            ? menuItems.map((item) => renderNavLink(item))
            : menuGroups.map((group) => {
                const hasActive = group.items.some((item) =>
                  navItemIsActive(locationPathname, item),
                );
                return (
                  <div
                    key={group.id}
                    className={`portal-nav-section portal-nav-section--group${
                      hasActive ? " portal-nav-section--active" : ""
                    }`}
                  >
                    <div className="portal-nav-section__heading" aria-hidden="true">
                      <span className="portal-nav-section__label">{group.label}</span>
                    </div>
                    <div className="portal-nav-section__items">
                      {group.items.map((item) => renderNavLink(item))}
                    </div>
                  </div>
                );
              })}
        </nav>

        <div className="portal-sidebar__footer">
          {releaseNotesPath ? (
            <NavLink
              to={releaseNotesPath}
              isActive={({ location }) => pathMatchesNavItem(location.pathname, releaseNotesPath)}
              className={({ isActive }) =>
                `portal-sidebar__version${isActive ? " portal-sidebar__version--active" : ""}`
              }
              title="Release notes"
            >
              {expanded ? versionLabel || `v${CURRENT_VERSION}` : "v"}
            </NavLink>
          ) : null}
          <button type="button" className="portal-sidebar__logout" onClick={onLogoutClick}>
            <FiLogOut className="portal-sidebar__logout-icon" aria-hidden="true" />
            {expanded ? <span>Sign out</span> : null}
          </button>
        </div>
      </aside>
    </>
  );
};

export default PortalSidebar;
